import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import { generationChanged } from '../utils/constants.js';

@customElement('sysml-simulation-panel')
export class SysmlSimulationPanel extends LitElement {
  @property({ attribute: false, hasChanged: generationChanged })
  modeler?: ModelerController;

  @property({ attribute: false })
  theme: 'light' | 'dark' = 'dark';

  @state() private _scenariosLoaded = false;

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      font-family: var(--r5-font-family, system-ui, -apple-system, sans-serif);
      color: var(--r5-text, #e0e0e0);
    }

    .sim-panel {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }

    .sim-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 12px;
      border-bottom: 1px solid var(--r5-border, #3e3e3e);
      flex-shrink: 0;
    }

    .sim-title {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      color: var(--r5-text-muted, #888);
      letter-spacing: 0.05em;
    }

    .sim-body {
      display: flex;
      flex: 1;
      overflow: hidden;
    }

    .sim-controls-area {
      flex-shrink: 0;
      border-bottom: 1px solid var(--r5-border, #3e3e3e);
    }

    .sim-trace-area {
      flex: 1;
      overflow: hidden;
      min-height: 0;
    }

    .sim-state-area {
      width: 240px;
      border-left: 1px solid var(--r5-border, #3e3e3e);
      overflow-y: auto;
      flex-shrink: 0;
    }

    .sim-summary {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 6px 12px;
      font-size: 11px;
      border-bottom: 1px solid var(--r5-border, #3e3e3e);
      flex-shrink: 0;
    }

    .summary-item {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .summary-label {
      color: var(--r5-text-muted, #888);
    }

    .summary-value {
      font-weight: 600;
      font-family: ui-monospace, SFMono-Regular, 'SF Mono', Consolas, monospace;
    }

    .summary-ok { color: #4ade80; }
    .summary-fail { color: #f87171; }
    .summary-neutral { color: var(--r5-text, #e0e0e0); }

    sysml-simulation-controls,
    sysml-simulation-trace,
    sysml-simulation-state {
      display: block;
      height: 100%;
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    if (this.modeler && !this._scenariosLoaded) {
      await this.modeler.loadSimulationScenarios();
      this._scenariosLoaded = true;
    }
  }

  async updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('modeler') && this.modeler && !this._scenariosLoaded) {
      await this.modeler.loadSimulationScenarios();
      this._scenariosLoaded = true;
    }
  }

  private _handlePlay = async (e: CustomEvent<string>) => {
    await this.modeler?.startSimulation(e.detail);
    this.requestUpdate();
  };

  private _handleReset = () => {
    this.modeler?.resetSimulation();
    this.requestUpdate();
  };

  render() {
    if (!this.modeler) return nothing;

    const m = this.modeler;
    const result = m.simulationResult;
    const summary = result?.summary;

    return html`
      <div class="sim-panel">
        <div class="sim-controls-area">
          <sysml-simulation-controls
            .status=${m.simulationStatus}
            .time=${m.simulationState?.time ?? 0}
            .stepCount=${summary?.totalSteps ?? 0}
            .scenarios=${m.simulationScenarios}
            .theme=${this.theme}
            @simulationPlay=${this._handlePlay}
            @simulationReset=${this._handleReset}
          ></sysml-simulation-controls>
        </div>

        ${summary ? html`
          <div class="sim-summary">
            <div class="summary-item">
              <span class="summary-label">Steps:</span>
              <span class="summary-value summary-neutral">${summary.totalSteps}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Time:</span>
              <span class="summary-value summary-neutral">${summary.totalTime.toFixed(1)}s</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Transitions:</span>
              <span class="summary-value summary-neutral">${summary.totalTransitions}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Constraints:</span>
              <span class="summary-value ${summary.constraintsViolated > 0 ? 'summary-fail' : 'summary-ok'}">
                ${summary.constraintsSatisfied}/${summary.constraintsSatisfied + summary.constraintsViolated}
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Requirements:</span>
              <span class="summary-value ${summary.requirementsUnsatisfied > 0 ? 'summary-fail' : 'summary-ok'}">
                ${summary.requirementsSatisfied}/${summary.requirementsSatisfied + summary.requirementsUnsatisfied}
              </span>
            </div>
          </div>
        ` : nothing}

        <div class="sim-body">
          <div class="sim-trace-area">
            <sysml-simulation-trace
              .entries=${m.simulationTraceEntries}
              .theme=${this.theme}
            ></sysml-simulation-trace>
          </div>
          <div class="sim-state-area">
            <sysml-simulation-state
              .variables=${m.simulationState?.variables ?? {}}
              .activeStates=${m.simulationState?.activeStates ?? {}}
              .constraintResults=${m.simulationConstraintResults}
              .requirementResults=${m.simulationRequirementResults}
              .theme=${this.theme}
            ></sysml-simulation-state>
          </div>
        </div>
      </div>
    `;
  }
}
